import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useTutorialSeen } from './use-tutorial-seen';
import style from './style.module.scss';
const MAX_BUBBLE_W = 264;
const BUBBLE_H = 170;
const GAP = 14;
const PAD = 8;
const EDGE = 8;
function getBubbleW() {
    return Math.min(MAX_BUBBLE_W, window.innerWidth - EDGE * 2);
}
function resolvePlace(place, rect, vw, vh) {
    const bw = getBubbleW();
    let p = place;
    if (p === 'bottom' && vh - rect.bottom < BUBBLE_H + GAP)
        p = rect.top > BUBBLE_H + GAP ? 'top' : 'right';
    if (p === 'top' && rect.top < BUBBLE_H + GAP)
        p = vh - rect.bottom > BUBBLE_H + GAP ? 'bottom' : 'right';
    if (p === 'right' && vw - rect.right < bw + GAP)
        p = rect.left > bw + GAP ? 'left' : 'bottom';
    if (p === 'left' && rect.left < bw + GAP)
        p = vw - rect.right > bw + GAP ? 'right' : 'bottom';
    return p;
}
function clampH(top, H) {
    return Math.max(EDGE, Math.min(H - BUBBLE_H - EDGE, top));
}
function computeLayout(s) {
    let el = null;
    for (const sel of s.selector.split(',')) {
        el = document.querySelector(sel.trim());
        if (el)
            break;
    }
    if (!el)
        return null;
    const r = el.getBoundingClientRect();
    const W = window.innerWidth;
    const H = window.innerHeight;
    const bw = getBubbleW();
    const ring = {
        left: r.left - PAD,
        top: r.top - PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
    };
    const place = resolvePlace(s.place, r, W, H);
    const cx = r.left + r.width / 2;
    const cy = r.top + Math.min(r.height / 2, BUBBLE_H / 2);
    let bubbleStyle = {};
    let arrowStyle = {};
    if (place === 'bottom') {
        const left = Math.max(EDGE, Math.min(W - bw - EDGE, cx - bw / 2));
        const anchorBottom = Math.min(r.bottom, r.top + BUBBLE_H);
        const top = Math.min(anchorBottom + GAP, H - BUBBLE_H - EDGE);
        bubbleStyle = { top, left };
        arrowStyle = { top: -6, left: Math.max(16, Math.min(bw - 24, cx - left - 6)) };
    }
    else if (place === 'top') {
        const left = Math.max(EDGE, Math.min(W - bw - EDGE, cx - bw / 2));
        const bottom = Math.min(H - r.top + GAP, H - EDGE);
        bubbleStyle = { bottom, left };
        arrowStyle = { bottom: -6, left: Math.max(16, Math.min(bw - 24, cx - left - 6)) };
    }
    else if (place === 'right') {
        const top = clampH(cy - BUBBLE_H / 2, H);
        const left = Math.min(r.right + GAP, W - bw - EDGE);
        bubbleStyle = { left, top };
        arrowStyle = { left: -6, top: Math.max(16, Math.min(BUBBLE_H - 24, cy - top - 6)) };
    }
    else {
        const top = clampH(cy - BUBBLE_H / 2, H);
        const right = Math.min(W - r.left + GAP, W - EDGE);
        const impliedLeft = W - right - bw;
        const clampedRight = impliedLeft < EDGE ? W - bw - EDGE : right;
        bubbleStyle = { right: clampedRight, top };
        arrowStyle = { right: -6, top: Math.max(16, Math.min(BUBBLE_H - 24, cy - top - 6)) };
    }
    return { ring, bubbleStyle, arrowStyle };
}
export default function TutorialSpotlight({ steps, storageKey }) {
    const [step, setStep] = useState(0);
    const { seen, markSeen } = useTutorialSeen(storageKey);
    const [dismissed, setDismissed] = useState(false);
    // Видимость реактивна к seen: если состояние «пройдено» приедет с сервера
    // (гидрирование профиля), подсказка скроется без перепоказа.
    const visible = !seen && !dismissed;
    const [layout, setLayout] = useState(null);
    const recompute = useCallback(() => {
        if (!visible)
            return;
        setLayout(computeLayout(steps[step]));
    }, [step, visible, steps]);
    useEffect(() => {
        if (!visible)
            return;
        const t = setTimeout(recompute, 100);
        window.addEventListener('resize', recompute);
        return () => {
            clearTimeout(t);
            window.removeEventListener('resize', recompute);
        };
    }, [step, visible, recompute]);
    const dismiss = () => {
        markSeen();
        setDismissed(true);
    };
    const next = () => (step < steps.length - 1 ? setStep((s) => s + 1) : dismiss());
    const prev = () => setStep((s) => Math.max(0, s - 1));
    if (!visible || steps.length === 0)
        return null;
    const t = steps[step];
    return (_jsxs(_Fragment, { children: [layout ? (_jsxs("svg", { style: {
                    position: 'fixed',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 90,
                }, "aria-hidden": true, children: [_jsx("defs", { children: _jsxs("mask", { id: "tut-hole", children: [_jsx("rect", { width: "100%", height: "100%", fill: "white" }), _jsx("rect", { x: layout.ring.left, y: layout.ring.top, width: layout.ring.width, height: layout.ring.height, rx: "9", fill: "black" })] }) }), _jsx("rect", { width: "100%", height: "100%", fill: "rgba(10,9,8,.52)", mask: "url(#tut-hole)" })] })) : (_jsx("div", { style: {
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(10,9,8,.52)',
                    pointerEvents: 'none',
                    zIndex: 90,
                } })), layout && (_jsx("div", { className: style['ring'], style: {
                    left: layout.ring.left,
                    top: layout.ring.top,
                    width: layout.ring.width,
                    height: layout.ring.height,
                } })), _jsxs("div", { className: style['bubble'], style: layout ? layout.bubbleStyle : { bottom: 80, right: 20 }, onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), children: [layout && _jsx("div", { className: style['arrow'], style: layout.arrowStyle }), _jsxs("div", { className: style['step-label'], children: ["\u041F\u043E\u0434\u0441\u043A\u0430\u0437\u043A\u0430 ", step + 1, " \u0438\u0437 ", steps.length] }), _jsx("div", { className: style['title'], children: t.title }), _jsx("div", { className: style['text'], children: t.text }), _jsxs("div", { className: style['footer'], children: [_jsx("div", { className: style['dots'], children: steps.map((_, i) => (_jsx("span", { className: [style['dot'], i === step ? style['active'] : '']
                                        .filter(Boolean)
                                        .join(' ') }, i))) }), _jsxs("div", { className: style['btns'], children: [step > 0 && (_jsx("button", { type: "button", className: style['btn'], onClick: prev, children: "\u2190 \u041D\u0430\u0437\u0430\u0434" })), _jsx("button", { type: "button", className: style['btn'], onClick: dismiss, children: "\u041F\u0440\u043E\u043F\u0443\u0441\u0442\u0438\u0442\u044C" }), _jsx("button", { type: "button", className: [style['btn'], style['next']].join(' '), onClick: next, children: step < steps.length - 1 ? 'Далее →' : 'Понятно ✓' })] })] })] })] }));
}
